import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { authHelpers } from "../../firebaseClient"
import { getUserOrgId, getOrganization, updateOrganizationProfile, claimOrganizationNesolearnId } from "../../utils/orgHelpers"
import { readImageAsBase64 } from "../../utils/imageHelpers"
import { normalizeUsername, isValidUsername } from "../../utils/nesolearnIdHelpers"

export const OrganizationFormPanel = () => {
  const navigate = useNavigate()

  // ---- form state / logic (unchanged) ----
  const [orgId, setOrgId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [logoError, setLogoError] = useState("")
  const [coverError, setCoverError] = useState("")
  const [ownerUid, setOwnerUid] = useState(null)
  const [claimUsername, setClaimUsername] = useState("")
  const [claimError, setClaimError] = useState("")
  const [alreadyClaimedId, setAlreadyClaimedId] = useState("")
  const [formData, setFormData] = useState({
    logoUrl: "",
    coverImageUrl: "",
    description: "",
    website: "",
    country: "",
    location: "",
    socialLinks: {
      twitter: "",
      linkedin: "",
    },
  })

  useEffect(() => {
    const loadOrg = async () => {
            const { session, error: sessionError } = await authHelpers.getSession()

      if (sessionError || !session) {
        navigate("/authentication-portal", { replace: true })
        return
      }

      setOwnerUid(session.user.uid)

      const { orgId: foundOrgId, error: orgIdError } = await getUserOrgId(session.user.uid)

      if (orgIdError || !foundOrgId) {
        navigate("/user-dashboard", { replace: true })
        return
      }

      setOrgId(foundOrgId)

            const { organization } = await getOrganization(foundOrgId)
      if (organization?.nesolearnId) setAlreadyClaimedId(organization.nesolearnId)
      if (organization) {
        setFormData((prev) => ({
          ...prev,
          logoUrl: organization?.logoUrl || "",
          coverImageUrl: organization?.coverImageUrl || "",
          description: organization?.description || "",
          website: organization?.website || "",
          country: organization?.country || "",
          location: organization?.location || "",
          socialLinks: {
            twitter: organization?.socialLinks?.twitter || "",
            linkedin: organization?.socialLinks?.linkedin || "",
          },
        }))
      }

      setLoading(false)
    }

    loadOrg()
  }, [navigate])

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e?.target?.value }))
  }

  const handleSocialChange = (platform) => (e) => {
    setFormData((prev) => ({
      ...prev,
      socialLinks: { ...prev?.socialLinks, [platform]: e?.target?.value },
    }))
  }

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return

    setLogoError("")
    const { base64, error } = await readImageAsBase64(file)

    if (error) {
      setLogoError(error?.message)
      return
    }

    setFormData((prev) => ({ ...prev, logoUrl: base64 }))
  }

  const handleCoverUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return

    setCoverError("")
    const { base64, error } = await readImageAsBase64(file)

    if (error) {
      setCoverError(error?.message)
      return
    }

    setFormData((prev) => ({ ...prev, coverImageUrl: base64 }))
  }

    // Every organization must claim a Nesolearn ID — this can't be skipped,
  // even though the rest of the profile (branding, description, etc.) can be.
  // Returns true if the org already has one, or successfully claims one now.
  const ensureNesolearnIdClaimed = async () => {
    if (alreadyClaimedId) return true

    const clean = normalizeUsername(claimUsername)
    if (!isValidUsername(clean)) {
      setClaimError("Use 3-20 characters, starting with a letter — letters, numbers, _ or - only.")
      return false
    }

    const { nesolearnId, error } = await claimOrganizationNesolearnId(orgId, ownerUid, formData?.contactEmail || "", clean)
    if (error) {
      setClaimError(error?.message || "Couldn't claim that ID. Try another.")
      return false
    }

    setAlreadyClaimedId(nesolearnId)
    setClaimError("")
    return true
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setSubmitError("")

    const claimed = await ensureNesolearnIdClaimed()
    if (!claimed) return

    setIsSubmitting(true)
    const { error } = await updateOrganizationProfile(orgId, formData)
    setIsSubmitting(false)

    if (error) {
      setSubmitError(error?.message || "Something went wrong. Please try again.")
      return
    }

    navigate("/organization-dashboard", { replace: true })
  }

  const handleSkip = async () => {
    const claimed = await ensureNesolearnIdClaimed()
    if (!claimed) return

    navigate("/organization-dashboard", { replace: true })
  }

  // ---- Mercury visual layer (performance-tuned) ----
  const blobsData = useMemo(() => {
    return Array.from({ length: 4 }).map(() => ({
      size: Math.random() * 200 + 150,
      left: Math.random() * 80 + 10,
      top: Math.random() * 80 + 10,
      animationDelay: Math.random() * -20,
      animationDuration: Math.random() * 15 + 15,
    }))
  }, [])

  const blobRefs = useRef([])

  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      return
    }

    let frame = null
    let latestEvent = null

    const applyParallax = () => {
      if (latestEvent) {
        const x = latestEvent.clientX / window.innerWidth
        const y = latestEvent.clientY / window.innerHeight
        blobRefs.current.forEach((blob, index) => {
          if (blob) {
            const speed = (index + 1) * 10
            blob.style.marginLeft = `${x * speed}px`
            blob.style.marginTop = `${y * speed}px`
          }
        })
      }
      frame = null
    }

    const handleMouseMove = (e) => {
      latestEvent = e
      if (frame === null) {
        frame = requestAnimationFrame(applyParallax)
      }
    }

    document.addEventListener("mousemove", handleMouseMove)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [])

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          background: "#050505",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#ffffff",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <style>{`
          @keyframes onboarding-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.15)",
              borderBottomColor: "#ffffff",
              animation: "onboarding-spin 0.8s linear infinite",
            }}
          />
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px" }}>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="mercury-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;800&family=Space+Mono&display=swap');

        .mercury-wrapper {
          --bg: #050505;
          --mercury: #e0e0e0;
          --accent: #ffffff;
          --text-dim: rgba(255, 255, 255, 0.5);
          --filter-goo: url('#onboarding-gooey');
          background-color: var(--bg);
          color: var(--accent);
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
          width: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 48px 0;
          position: relative;
        }

        .mercury-wrapper * {
          box-sizing: border-box;
          -webkit-font-smoothing: antialiased;
        }

        .mercury-wrapper .stage {
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 0;
          filter: var(--filter-goo);
          opacity: 0.6;
        }

        .mercury-wrapper .blob {
          position: absolute;
          background: linear-gradient(135deg, var(--mercury), #888);
          border-radius: 50%;
          filter: blur(14px);
          animation: onboarding-float 24s infinite alternate ease-in-out;
          will-change: transform, margin;
          box-shadow: inset -10px -10px 20px rgba(0,0,0,0.5),
                      10px 10px 30px rgba(255,255,255,0.2);
          transition: margin 0.15s ease-out;
        }

        @keyframes onboarding-float {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(10vw, 20vh) scale(1.2); }
          66% { transform: translate(-5vw, 10vh) scale(0.8); }
          100% { transform: translate(5vw, -10vh) scale(1.1); }
        }

        .mercury-wrapper .auth-container {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 560px;
          padding: 0 40px;
        }

        .mercury-wrapper .header {
          margin-bottom: 40px;
          text-align: left;
        }

        .mercury-wrapper .brand-id {
          font-family: 'Space Mono', monospace;
          font-size: 10px;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: var(--text-dim);
          margin-bottom: 8px;
          display: block;
        }

        .mercury-wrapper .header h1 {
          font-weight: 800;
          font-size: 2.5rem;
          line-height: 0.95;
          letter-spacing: -2px;
          margin-left: -2px;
          margin-top: 0;
          margin-bottom: 12px;
        }

        .mercury-wrapper .header p {
          color: var(--text-dim);
          font-size: 14px;
          line-height: 1.5;
          max-width: 420px;
        }

        .mercury-wrapper .section-label {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 2px;
          margin: 0 0 16px;
          padding-top: 20px;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .mercury-wrapper .section-label:first-of-type {
          border-top: none;
          padding-top: 0;
        }

        .mercury-wrapper .form-group {
          position: relative;
          margin-bottom: 26px;
          transition: transform 0.4s cubic-bezier(0.2, 1, 0.3, 1);
        }

        .mercury-wrapper .form-group:focus-within {
          transform: translateX(10px);
        }

        .mercury-wrapper .form-group label {
          display: block;
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: var(--text-dim);
          margin-bottom: 12px;
          text-transform: uppercase;
        }

        .mercury-wrapper .form-group input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          color: var(--accent);
          padding: 12px 0;
          font-size: 18px;
          outline: none;
          transition: border-color 0.4s;
          font-family: 'Inter', sans-serif;
        }

        .mercury-wrapper .input-glow {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0%;
          height: 2px;
          background: var(--mercury);
          transition: width 0.6s cubic-bezier(0.2, 1, 0.3, 1);
          box-shadow: 0 0 15px var(--mercury);
        }

        .mercury-wrapper .form-group input:focus + .input-glow {
          width: 100%;
        }

        .mercury-wrapper .form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0 24px;
        }

        @media (min-width: 640px) {
          .mercury-wrapper .form-row {
            grid-template-columns: 1fr 1fr;
          }
        }

        .mercury-wrapper .file-group {
          margin-bottom: 30px;
        }

        .mercury-wrapper .file-group label {
          display: block;
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: var(--text-dim);
          margin-bottom: 12px;
          text-transform: uppercase;
        }

        .mercury-wrapper .file-preview {
          display: block;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.1);
          object-fit: cover;
          margin-bottom: 12px;
        }

        .mercury-wrapper .file-preview.logo {
          width: 72px;
          height: 72px;
        }

        .mercury-wrapper .file-preview.cover {
          width: 100%;
          height: 120px;
        }

        .mercury-wrapper input[type="file"] {
          color: var(--text-dim);
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          width: 100%;
        }

        .mercury-wrapper input[type="file"]::file-selector-button,
        .mercury-wrapper input[type="file"]::-webkit-file-upload-button {
          background: var(--accent);
          color: #000;
          border: none;
          padding: 8px 16px;
          margin-right: 12px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
        }

        .mercury-wrapper .file-hint {
          font-size: 12px;
          color: var(--text-dim);
          margin-top: 8px;
        }

        .mercury-wrapper .field-error {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: #ff8080;
          margin-top: 8px;
        }

        .mercury-wrapper .submit-error {
          font-family: 'Space Mono', monospace;
          font-size: 12px;
          color: #ff8080;
          margin-top: -10px;
          margin-bottom: 20px;
        }

        .mercury-wrapper .actions-row {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-top: 40px;
          flex-wrap: wrap;
        }

        .mercury-wrapper .submit-wrap {
          position: relative;
        }

        .mercury-wrapper .btn-base {
          background: var(--accent);
          color: #000;
          border: none;
          padding: 18px 36px;
          font-size: 14px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 2px;
          cursor: pointer;
          position: relative;
          z-index: 2;
          transition: letter-spacing 0.3s, opacity 0.3s;
        }

        .mercury-wrapper .btn-base:hover {
          letter-spacing: 4px;
        }

        .mercury-wrapper .btn-base:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mercury-wrapper .mercury-drop {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          background: var(--mercury);
          transform: translate(-50%, -50%);
          z-index: 1;
          border-radius: 50px;
          transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .mercury-wrapper .submit-wrap:hover .mercury-drop {
          transform: translate(-50%, -50%) scale(1.05, 1.2);
          filter: brightness(1.2);
        }

        .mercury-wrapper .btn-skip {
          background: none;
          border: none;
          color: var(--text-dim);
          text-decoration: underline;
          font-size: 13px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
        }

        .mercury-wrapper .btn-skip:hover {
          color: var(--accent);
        }

        .mercury-wrapper .svg-filter-hidden {
          position: absolute;
          width: 0;
          height: 0;
        }
      `}</style>

      <svg className="svg-filter-hidden">
        <defs>
          <filter id="onboarding-gooey">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
          </filter>
        </defs>
      </svg>

      <div className="stage">
        {blobsData.map((data, index) => (
          <div
            key={index}
            ref={(el) => (blobRefs.current[index] = el)}
            className="blob"
            style={{
              width: `${data.size}px`,
              height: `${data.size}px`,
              left: `${data.left}%`,
              top: `${data.top}%`,
              animationDelay: `${data.animationDelay}s`,
              animationDuration: `${data.animationDuration}s`,
            }}
          />
        ))}
      </div>

      <main className="auth-container">
        <header className="header">
          <span className="brand-id">Nesolearn / Institution Access</span>
          <h1>COMPLETE<br/>YOUR PROFILE</h1>
          <p>This information appears on your public organization page and on certificates you issue.</p>
        </header>

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="file-group">
            <label>Organization Logo</label>
            {formData?.logoUrl && (
              <img src={formData?.logoUrl} alt="Logo preview" className="file-preview logo" />
            )}
            <input type="file" accept="image/*" onChange={handleLogoUpload} />
            <p className="file-hint">Max 300KB. Square images work best.</p>
            {logoError && <div className="field-error">{logoError}</div>}
          </div>

          <div className="file-group">
            <label>Cover Image</label>
            {formData?.coverImageUrl && (
              <img src={formData?.coverImageUrl} alt="Cover preview" className="file-preview cover" />
            )}
            <input type="file" accept="image/*" onChange={handleCoverUpload} />
            <p className="file-hint">Max 300KB. Wide images work best.</p>
            {coverError && <div className="field-error">{coverError}</div>}
          </div>

                    <div className="form-group">
            <label>Organization Nesolearn ID {!alreadyClaimedId && <span style={{ color: "#dc2626" }}>*</span>}</label>
            {alreadyClaimedId ? (
              <input type="text" value={alreadyClaimedId} disabled />
            ) : (
              <>
                <input
                  type="text"
                  placeholder="yourorg"
                  value={claimUsername}
                  onChange={(e) => setClaimUsername(e?.target?.value)}
                />
                <p className="file-hint">This is your organization's digital identity across Nesolearn — required, and can't be changed once claimed.</p>
                {claimError && <div className="field-error">{claimError}</div>}
              </>
            )}
            <div className="input-glow"></div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              placeholder="What does your organization do?"
              value={formData?.description}
              onChange={handleChange("description")}
            />
            <div className="input-glow"></div>
          </div>

          <div className="form-group">
            <label>Website</label>
            <input
              type="text"
              placeholder="https://yourorganization.com"
              value={formData?.website}
              onChange={handleChange("website")}
            />
            <div className="input-glow"></div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                placeholder="e.g. Ghana"
                value={formData?.country}
                onChange={handleChange("country")}
              />
              <div className="input-glow"></div>
            </div>
            <div className="form-group">
              <label>Location / City</label>
              <input
                type="text"
                placeholder="e.g. Accra"
                value={formData?.location}
                onChange={handleChange("location")}
              />
              <div className="input-glow"></div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Twitter / X</label>
              <input
                type="text"
                placeholder="https://x.com/..."
                value={formData?.socialLinks?.twitter}
                onChange={handleSocialChange("twitter")}
              />
              <div className="input-glow"></div>
            </div>
            <div className="form-group">
              <label>LinkedIn</label>
              <input
                type="text"
                placeholder="https://linkedin.com/..."
                value={formData?.socialLinks?.linkedin}
                onChange={handleSocialChange("linkedin")}
              />
              <div className="input-glow"></div>
            </div>
          </div>

          {submitError && <div className="submit-error">{submitError}</div>}

          <div className="actions-row">
            <div className="submit-wrap">
              <div className="mercury-drop"></div>
              <button type="submit" className="btn-base" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save and Continue"}
              </button>
            </div>
            <button type="button" className="btn-skip" onClick={handleSkip}>
              Skip for now
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}

export default OrganizationFormPanel